import type { Page, SessionUser } from '@salary/contracts'

const TOKEN_KEY = 'salary.session'

/**
 * The error the whole interface reacts to.
 *
 * Carries the status and the API's own error code, because the three cases a
 * user experiences differently — your session expired, you are not allowed to
 * do that, that clashes with something — are all "a failed request" to `fetch`
 * and must not be to the person reading the screen.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }
}

export const tokenStore = {
  read(): string | undefined {
    // Wrapped because storage throws outright in a private window with site
    // data blocked, and a sign-in page that cannot render is worse than one
    // that cannot remember you.
    try {
      return window.localStorage.getItem(TOKEN_KEY) ?? undefined
    } catch {
      return undefined
    }
  },
  write(token: string): void {
    try {
      window.localStorage.setItem(TOKEN_KEY, token)
    } catch {
      /* The session still works for this tab; it just will not survive a reload. */
    }
  },
  clear(): void {
    try {
      window.localStorage.removeItem(TOKEN_KEY)
    } catch {
      /* Nothing to do. */
    }
  },
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH'
  body?: unknown
  /** Omitted for sign-in, which is the one call that has no token yet. */
  anonymous?: boolean
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = options.anonymous ? undefined : tokenStore.read()

  const response = await fetch(path, {
    method: options.method ?? 'GET',
    headers: {
      ...(options.body ? { 'content-type': 'application/json' } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    ...(options.body ? { body: JSON.stringify(options.body) } : {}),
  })

  if (response.status === 204) return undefined as T

  const payload: unknown = await response.json().catch(() => undefined)

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string; details?: unknown } })
      ?.error
    throw new ApiError(
      response.status,
      error?.code ?? 'UNKNOWN',
      error?.message ?? `Request failed with status ${response.status}`,
      error?.details,
    )
  }

  return payload as T
}

/** Drops empty values so the URL carries only filters that are actually set. */
export function queryString(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue
    search.set(key, String(value))
  }
  const query = search.toString()
  return query ? `?${query}` : ''
}

export const api = {
  signIn: (email: string, password: string) =>
    request<{ token: string; user: SessionUser }>('/api/auth/login', {
      method: 'POST',
      body: { email, password },
      anonymous: true,
    }),

  me: () => request<{ user: SessionUser }>('/api/auth/me'),

  roster: (params: Record<string, string | number | undefined>) =>
    request<Page<RosterItem>>(`/api/employees${queryString(params)}`),

  filterOptions: () => request<FilterOptions>('/api/employees/filters'),

  employee: (id: string, asOf?: string) =>
    request<EmployeeDetail>(`/api/employees/${id}${queryString({ asOf })}`),

  createEmployee: (body: unknown) =>
    request<{ id: string }>('/api/employees', { method: 'POST', body }),

  updateEmployee: (id: string, body: unknown) =>
    request<{ id: string }>(`/api/employees/${id}`, { method: 'PATCH', body }),

  createRevision: (id: string, body: unknown) =>
    request<{ id: string }>(`/api/employees/${id}/revisions`, { method: 'POST', body }),

  overview: (params: Record<string, string | undefined>) =>
    request<Overview>(`/api/analytics/overview${queryString(params)}`),

  distribution: (params: Record<string, string | undefined>) =>
    request<DistributionResponse>(`/api/analytics/distribution${queryString(params)}`),

  bandOutliers: (params: Record<string, string | number | undefined>) =>
    request<BandOutliers>(`/api/analytics/band-outliers${queryString(params)}`),

  payEquity: (params: Record<string, string | number | undefined>) =>
    request<PayEquity>(`/api/analytics/pay-equity${queryString(params)}`),

  checkOffer: (params: Record<string, string | number | undefined>) =>
    request<OfferCheck>(`/api/analytics/offer${queryString(params)}`),

  simulateRaise: (body: unknown) =>
    request<RaiseSimulation>('/api/analytics/raise-simulation', { method: 'POST', body }),

  audit: (params: Record<string, string | number | undefined>) =>
    request<Page<AuditEntry>>(`/api/audit${queryString(params)}`),

  importRevisions: (csv: string, dryRun: boolean) =>
    request<ImportReport>('/api/import/revisions', {
      method: 'POST',
      body: { csv, dryRun },
    }),

  /**
   * Downloads a file that needs a bearer token.
   *
   * A plain link cannot do this: the browser sends no Authorization header, so
   * the endpoint would answer 401 and the user would get a file containing an
   * error. Fetching it and handing the browser a blob is the only way to
   * download from an authenticated endpoint without putting the token in a
   * query string, where it would end up in logs and in browser history.
   */
  download: async (path: string): Promise<void> => {
    const token = tokenStore.read()
    const response = await fetch(path, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    })

    if (!response.ok) {
      const payload = (await response.json().catch(() => undefined)) as
        { error?: { code?: string; message?: string } } | undefined
      throw new ApiError(
        response.status,
        payload?.error?.code ?? 'UNKNOWN',
        payload?.error?.message ?? 'The download failed',
      )
    }

    const disposition = response.headers.get('content-disposition') ?? ''
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'export.csv'

    const url = URL.createObjectURL(await response.blob())
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.append(link)
    link.click()
    link.remove()
    // Revoked, or every export leaks its own file until the tab is closed.
    URL.revokeObjectURL(url)
  },
}

// ---------------------------------------------------------------------------
// Response shapes
//
// Written out rather than inferred from the service, because these describe
// what crosses the wire. Where a field is optional here it is because the API
// genuinely omits it — an employee not yet paid on the requested date has no
// salary, and the interface has to say so rather than render a zero.
// ---------------------------------------------------------------------------

export interface FilterOptions {
  countries: string[]
  departments: string[]
  levels: string[]
  jobTitles: string[]
  /** Where most people actually work. Forms default to it rather than guessing. */
  primaryCountry?: string
  primaryDepartment?: string
}

export interface MoneyValue {
  minorUnits: number
  currency: string
}

export type BandPosition = 'BELOW_MINIMUM' | 'WITHIN_BAND' | 'ABOVE_MAXIMUM'

export interface RosterItem {
  id: string
  employeeNumber: string
  firstName: string
  lastName: string
  email: string
  countryCode: string
  department: string
  level: string
  jobTitle: string
  status: 'ACTIVE' | 'TERMINATED'
  hireDate: string
  currentSalary?: {
    base: MoneyValue
    variable: MoneyValue
    allowances: MoneyValue
    total: MoneyValue
  }
  totalInBaseCurrency?: MoneyValue
  compaRatio?: number
  bandPosition?: BandPosition
}

export interface RevisionView {
  id: string
  effectiveFrom: string
  base: MoneyValue
  variable: MoneyValue
  allowances: MoneyValue
  total: MoneyValue
  reason: string
  note?: string
  recordedAt: string
  recordedBy: string
  changeFromPrevious?: number
}

export interface EmployeeDetail {
  id: string
  employeeNumber: string
  firstName: string
  lastName: string
  email: string
  countryCode: string
  department: string
  level: string
  jobTitle: string
  managerId?: string
  gender?: string
  hireDate: string
  terminationDate?: string
  status: 'ACTIVE' | 'TERMINATED'
  asOf: string
  currentSalary?: {
    base: MoneyValue
    variable: MoneyValue
    allowances: MoneyValue
    total: MoneyValue
  }
  band?: {
    minimum: MoneyValue
    midpoint: MoneyValue
    maximum: MoneyValue
    compaRatio?: number
    position?: BandPosition
  }
  revisions: RevisionView[]
}

export interface Distribution {
  count: number
  currency: string
  total: MoneyValue
  mean: MoneyValue
  minimum: MoneyValue
  p10: MoneyValue
  p25: MoneyValue
  median: MoneyValue
  p75: MoneyValue
  p90: MoneyValue
  maximum: MoneyValue
}

export interface Breakdown {
  key: string
  headcount: number
  annual?: MoneyValue
  median?: MoneyValue
  share?: number
}

export interface Overview {
  asOf: string
  currency: string
  headcount: { total: number; paid: number; notCosted: number }
  payroll: { annual: MoneyValue; monthly: MoneyValue; mean?: MoneyValue; median?: MoneyValue }
  distribution?: Distribution
  byCountry: Breakdown[]
  byDepartment: Breakdown[]
  byLevel: Breakdown[]
  byJobTitle: Breakdown[]
}

export interface DistributionResponse {
  asOf: string
  currency: string
  basis: 'BASE' | 'TOTAL'
  groupBy?: string
  overall?: Distribution
  groups?: { key: string; headcount: number; distribution?: Distribution }[]
}

export interface BandOutlierItem {
  id: string
  countryCode: string
  department: string
  level: string
  basePay: MoneyValue
  band: { minimum: MoneyValue; midpoint: MoneyValue; maximum: MoneyValue }
  compaRatio: number
  position: 'BELOW_MINIMUM' | 'ABOVE_MAXIMUM'
  distanceToBand: MoneyValue
}

export interface BandOutliers {
  asOf: string
  summary: { belowMinimum: number; withinBand: number; aboveMaximum: number; notAssessable: number }
  items: BandOutlierItem[]
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}

export interface EquityFinding {
  cohort: string
  status: 'COMPARABLE' | 'GAP_IDENTIFIED' | 'SUPPRESSED_SMALL_SAMPLE'
  gap?: number
  note?: string
  reference?: { label: string; count: number; median: MoneyValue }
  comparison?: { label: string; count: number; median: MoneyValue }
}

export interface PayEquity {
  asOf: string
  currency: string
  basis: string
  reference: string
  comparison: string
  summary: {
    cohortsExamined: number
    gapsIdentified: number
    comparable: number
    suppressed: number
  }
  findings: EquityFinding[]
}

export interface RaiseSimulation {
  asOf: string
  currency: string
  scenario: { percentage: number; bringToBandMinimum: boolean }
  notCosted: number
  summary?: {
    count: number
    currentAnnual: MoneyValue
    proposedAnnual: MoneyValue
    increaseAnnual: MoneyValue
    increaseMonthly: MoneyValue
    effectiveRate: number
    liftedToMinimum: number
    enteredBand: number
  }
  byCountry: {
    key: string
    headcount: number
    currentAnnual: MoneyValue
    increaseAnnual: MoneyValue
  }[]
}

export interface OfferCheck {
  asOf: string
  cohort: string
  offer: MoneyValue
  offerInBaseCurrency?: MoneyValue
  bandAvailable: boolean
  band?: {
    minimum: MoneyValue
    midpoint: MoneyValue
    maximum: MoneyValue
    compaRatio: number
    position: BandPosition
    penetration: number
  }
  team?: {
    headcount: number
    median: MoneyValue
    p25: MoneyValue
    p75: MoneyValue
    minimum: MoneyValue
    maximum: MoneyValue
    aboveProportion?: number
    highestInCohort: boolean
  }
}

export interface ImportProblem {
  line: number
  employeeNumber?: string
  code: string
  message: string
}

export interface ImportReport {
  dryRun: boolean
  totalRows: number
  acceptedRows: number
  problems: ImportProblem[]
  applied?: number
  effectiveRange?: { from: string; to: string }
  increaseAnnual?: MoneyValue
}

export interface AuditEntry {
  id: string
  actorId: string
  actorEmail: string
  action: string
  entityType: string
  entityId: string
  employeeId?: string
  before?: Record<string, unknown>
  after?: Record<string, unknown>
  at: string
}
